import { Box, Button, Chip, Divider } from "@mui/material";
import dayjs from "dayjs";
import React from "react";
import Flex from "../../../../../components/Flex";
import Text from "../../../../../components/Text";
import {
  APPOINTMENT_STATUS_RECORD,
  COLOR_MAPPER,
} from "../../../../../pages/app/provider/scheduling/appointment/AppointmentScheduling";
import { Status } from "../../../../../dummy-data/appointment-list-data-scheduling";
import {
  APPOINTMENT_MODE_COLORS,
  BUTTON_STYLES,
  CARD_STYLES,
  COLORS,
} from "../constants";
import { Visit, VisitActions } from "../patient-portal-dashboard.types";

interface VisitHistoryItemProps {
  visit: Visit;
  actions?: VisitActions;
}

const VisitHistoryItem: React.FC<VisitHistoryItemProps> = ({
  visit,
  actions,
}) => {
  const isUpcoming = visit.startTime
    ? dayjs(visit.startTime).isAfter(dayjs())
    : false;
  const handleViewSummary = () => actions?.onViewSummary?.(visit.uuid);
  const handleViewDetails = () => actions?.onViewDetails?.(isUpcoming);
  const visitTypeLabel = APPOINTMENT_MODE_COLORS[visit.mode]?.label ?? "-";
  const isCompleted = visit.status === "COMPLETED";

  const start = visit.startTime ? dayjs(visit.startTime) : null;
  const formattedDate = start?.isValid() ? start.format("DD MMM YYYY") : "";
  const formattedTime = start?.isValid() ? start.format("hh:mm A") : "";

  const visitTitle = visit.chiefComplaint?.trim() || "";
  const providerName = visit.providerName?.trim() || "";

  const statusLabel =
    APPOINTMENT_STATUS_RECORD[visit.status as Status] ??
    String(visit.status ?? "").replaceAll("_", " ");
  const statusColors = COLOR_MAPPER[statusLabel];

  return (
    <Box
      sx={{
        ...CARD_STYLES,
        width: "100%",
        p: 1.5,
        "&:hover": CARD_STYLES.hover,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 0.75,
        }}
      >
        {(formattedDate || formattedTime) && (
          <Text
            size="12px"
            c={COLORS.textPrimary}
            sx={{ fontWeight: 600, flex: 1, pr: 1 }}
          >
            {formattedDate}
            {formattedTime ? ` | ${formattedTime}` : ""}
          </Text>
        )}
        <Chip
          label={statusLabel}
          size="small"
          sx={{
            color: statusColors?.color,
            backgroundColor: statusColors?.bgColor,
            fontSize: "11px",
            fontWeight: 500,
            height: "20px",
            "& .MuiChip-label": { px: 0.75 },
          }}
        />
      </Box>
      {visitTitle && (
        <Text size="13px" c={COLORS.textSecondary} sx={{ mb: 0.5 }}>
          {visitTitle}
        </Text>
      )}
      {providerName && (
        <Text size="13px" c={COLORS.textSecondary} sx={{ mb: 0.5 }}>
          {`Provider : ${providerName}`}
        </Text>
      )}
      <Text size="13px" c={COLORS.textSecondary} sx={{ mb: 1.5 }}>
        {`Visit Type : ${visitTypeLabel}`}
      </Text>
      <Divider />
      <Flex width="100%" justifyContent="flex-end" pt={1}>
        {isCompleted ? (
          <Button
            variant="outlined"
            size="small"
            sx={{
              ...BUTTON_STYLES.outlined,
              minWidth: "120px",
            }}
            onClick={handleViewSummary}
          >
            View Summary
          </Button>
        ) : (
          <Button
            variant="outlined"
            size="small"
            sx={{
              ...BUTTON_STYLES.outlined,
              minWidth: "120px",
            }}
            onClick={handleViewDetails}
          >
            View Details
          </Button>
        )}
      </Flex>
    </Box>
  );
};

export default VisitHistoryItem;
